import { FC, useState, useEffect } from "react";
import { useNavigate, useLocation, useParams, Link } from "react-router-dom";
import { KTSVG } from "../../../../../../_metronic/helpers";
import axios from "axios";
import Swal from "sweetalert2";
import { getAuth } from "../../../../../modules/auth";
import { Modal, Button } from "react-bootstrap";

interface LocationState {
  moduleId?: number;
  initialTab?: string;
}

export const CampeonatoForm: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const auth = getAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  const S3_BASE_URL =
    process.env.REACT_APP_BUCKET_AWS_URL + "/convocations/logos";

  const state = location.state as LocationState;
  const moduleId = state?.moduleId ? Number(state.moduleId) : 1;

  const [activeTab, setActiveTab] = useState(state?.initialTab || "DADOS");

  const [loading, setLoading] = useState(false);
  const [categorias, setCategorias] = useState<any[]>([]);
  const [jogos, setJogos] = useState<any[]>([]);

  const [showConfirmConvocacaoModal, setShowConfirmConvocacaoModal] =
    useState(false);
  const [convocadosParaConfirmar, setConvocadosParaConfirmar] = useState<any[]>(
    [],
  );
  const [selectedConvocacaoId, setSelectedConvocacaoId] = useState<
    number | null
  >(null);
  const [modalActionLoading, setModalActionLoading] = useState(false);

  const [formData, setFormData] = useState({
    nome: "",
    organizador: "",
    valor_taxa: "",
    categoria: "",
    data_inicio: "",
    data_fim: "",
    observacoes: "",
    status: "agendado",
    logo_url: "",
  });

  const [logoFile, setLogoFile] = useState<File | null>(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!auth?.api_token) return;
      const headers = { Authorization: `Bearer ${auth.api_token}` };

      try {
        const catResponse = await axios.get(
          `${apiUrl}/technical-committee/categories`,
          { headers },
        );
        setCategorias(catResponse.data);

        if (id) {
          setLoading(true);
          const champResponse = await axios.get(
            `${apiUrl}/technical-committee/championships/${id}`,
            { headers },
          );
          const data = champResponse.data;

          const formatData = (isoString: string) =>
            isoString ? isoString.split("T")[0] : "";

          setFormData({
            nome: data.nome,
            organizador: data.organizador || "",
            valor_taxa: data.valor_taxa || "",
            categoria: data.categoria,
            data_inicio: formatData(data.data_inicio),
            data_fim: formatData(data.data_fim),
            status: data.status,
            observacoes: data.observacoes || "",
            logo_url: data.logo_url || "",
          });

          const jogosResponse = await axios.get(
            `${apiUrl}/technical-committee/getconvocations`,
            {
              params: { moduleId },
              headers,
            },
          );

          const todosJogos = jogosResponse.data;
          const jogosDesteCampeonato = todosJogos.filter(
            (j: any) =>
              j._id_campeonato === Number(id) ||
              j.campeonato_nome === data.nome,
          );

          setJogos(jogosDesteCampeonato);
          setLoading(false);
        }
      } catch (error) {
        console.error("Erro ao carregar dados", error);
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [apiUrl, auth?.api_token, id, moduleId]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nome || !formData.categoria || !formData.data_inicio) {
      Swal.fire("Atenção", "Preencha os campos obrigatórios (*)", "warning");
      return;
    }

    setLoading(true);
    const headers = {
      Authorization: `Bearer ${auth?.api_token}`,
      "Content-Type": "multipart/form-data",
    };

    const payload = new FormData();
    payload.append("nome", formData.nome);
    payload.append("organizador", formData.organizador);
    payload.append("valor_taxa", formData.valor_taxa);
    payload.append("categoria", formData.categoria);
    payload.append("data_inicio", formData.data_inicio);
    payload.append("data_fim", formData.data_fim);
    payload.append("observacoes", formData.observacoes);
    payload.append("status", formData.status);
    payload.append("_id_modulo", String(moduleId));

    if (logoFile) {
      payload.append("file", logoFile);
    }

    try {
      if (id) {
        await axios.put(
          `${apiUrl}/technical-committee/championships/${id}`,
          payload,
          { headers },
        );
        Swal.fire({
          icon: "success",
          title: "Campeonato atualizado!",
          showConfirmButton: false,
          timer: 1500,
        });
      } else {
        const res = await axios.post(
          `${apiUrl}/technical-committee/championships`,
          payload,
          { headers },
        );
        Swal.fire({
          icon: "success",
          title: "Campeonato cadastrado!",
          text: "Agora você pode adicionar jogos.",
          showConfirmButton: true,
        });

        const newId = res.data.insertId || res.data._id_campeonato;
        if (newId)
          navigate(`/technical-committee/championships/edit/${newId}`, {
            state: { moduleId },
          });
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Erro", "Falha ao salvar campeonato.", "error");
    } finally {
      setLoading(false);
    }
  };

  const openConfirmConvocacao = async (convocacaoId: number) => {
    setSelectedConvocacaoId(convocacaoId);
    setModalActionLoading(true);
    try {
      const response = await axios.get(
        `${apiUrl}/technical-committee/convocations/${convocacaoId}/athletes`,
        { headers: { Authorization: `Bearer ${auth?.api_token}` } },
      );
      setConvocadosParaConfirmar(response.data || []);
      setShowConfirmConvocacaoModal(true);
    } catch (error) {
      Swal.fire("Erro", "Falha ao buscar atletas selecionados.", "error");
    } finally {
      setModalActionLoading(false);
    }
  };

  const processConvocacaoFinal = async () => {
    if (!selectedConvocacaoId) return;
    setModalActionLoading(true);
    try {
      await axios.put(
        `${apiUrl}/technical-committee/convocations/${selectedConvocacaoId}/confirm-all`,
        { status: "convocado" },
        { headers: { Authorization: `Bearer ${auth?.api_token}` } },
      );

      Swal.fire("Sucesso!", "Atletas convocados com sucesso.", "success");
      setShowConfirmConvocacaoModal(false);
    } catch (error) {
      Swal.fire("Erro", "Falha ao processar convocação.", "error");
    } finally {
      setModalActionLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("pt-BR") +
      " " +
      date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    );
  };

  return (
    <div className="card shadow-sm w-100">
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-center flex-row">
          {id && formData.logo_url && (
            <div
              className="symbol symbol-50px symbol-circle me-5"
              style={{ width: "50px" }}
            >
              <img
                src={formData.logo_url}
                alt="Logo do Campeonato"
                className="w-100 h-100 object-fit-cover"
              />
            </div>
          )}

          <div className="d-flex flex-column align-items-start">
            <span className="card-label fw-bold fs-3 mb-1">
              {id ? formData.nome : "Novo Campeonato"}
            </span>
            <span className="text-muted fw-semibold fs-7">
              {id ? "Edição de Campeonato" : "Cadastrar novo evento"}
            </span>
          </div>
        </h3>

        <div className="card-toolbar">
          <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0">
            <li className="nav-item">
              <p
                className={`nav-link cursor-pointer ${
                  activeTab === "DADOS" && "active"
                }`}
                onClick={() => setActiveTab("DADOS")}
              >
                Dados Gerais
              </p>
            </li>
            <li className="nav-item">
              <p
                className={`nav-link cursor-pointer ${
                  activeTab === "JOGOS" && "active"
                } ${!id && "disabled opacity-50"}`}
                onClick={() => id && setActiveTab("JOGOS")}
              >
                Jogos / Treinos
              </p>
            </li>
            <li className="nav-item">
              <p
                className={`nav-link cursor-pointer ${
                  activeTab === "CONVOCACAO" && "active"
                } ${!id && "disabled opacity-50"}`}
                onClick={() => id && setActiveTab("CONVOCACAO")}
              >
                Convocação
              </p>
            </li>
          </ul>
          <button
            type="button"
            className="btn btn-light btn-sm me-3 ms-3"
            onClick={() => navigate("/technical-committee")}
          >
            Voltar
          </button>
        </div>
      </div>

      <div className="card-body w-100 pt-2">
        {activeTab === "DADOS" && (
          <form onSubmit={handleSave}>
            <div className="row g-5">
              <div className="col-md-4">
                <label className="form-label required">
                  Nome do Campeonato
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Ex: Copa São Paulo Jr"
                  value={formData.nome}
                  onChange={(e) =>
                    setFormData({ ...formData, nome: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3">
                <label className="form-label required">Categoria</label>
                <select
                  className="form-select form-select-sm"
                  value={formData.categoria}
                  onChange={(e) =>
                    setFormData({ ...formData, categoria: e.target.value })
                  }
                >
                  <option value="">Selecione...</option>
                  {categorias.map((cat) => (
                    <option key={cat._id_categoria} value={cat.nome}>
                      {cat.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Organizador</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Ex: FPF, CBF"
                  value={formData.organizador}
                  onChange={(e) =>
                    setFormData({ ...formData, organizador: e.target.value })
                  }
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Valor Taxa</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Ex: FPF, CBF"
                  value={formData.valor_taxa}
                  onChange={(e) =>
                    setFormData({ ...formData, valor_taxa: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3">
                <label className="form-label required">Data Início</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={formData.data_inicio}
                  onChange={(e) =>
                    setFormData({ ...formData, data_inicio: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Data Fim</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={formData.data_fim}
                  onChange={(e) =>
                    setFormData({ ...formData, data_fim: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Status</label>
                <select
                  className="form-select form-select-sm"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  <option value="agendado">Agendado</option>
                  <option value="ativo">Em Andamento</option>
                  <option value="finalizado">Finalizado</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Foto / Logo</label>
                <input
                  type="file"
                  className="form-control form-control-sm border border-gray-300"
                  style={{
                    opacity: 1,
                    width: "100%",
                    height: "auto",
                    position: "relative",
                  }}
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setLogoFile(e.target.files[0]);
                    }
                  }}
                />
              </div>
              <div className="col-md-12">
                <label className="form-label">Observações</label>
                <textarea
                  rows={4}
                  className="form-control form-control-sm"
                  value={formData.observacoes}
                  onChange={(e) =>
                    setFormData({ ...formData, observacoes: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="d-flex justify-content-end mt-10">
              <button
                type="button"
                className="btn btn-light btn-sm me-3"
                onClick={() => navigate("/technical-committee")}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={loading}
              >
                {loading
                  ? "Salvando..."
                  : id
                  ? "Atualizar"
                  : "Salvar e Continuar"}
              </button>
            </div>
          </form>
        )}

        {/* ABA JOGOS */}
        {activeTab === "JOGOS" && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="fw-bold m-0 text-gray-800 fs-5">
                Lista de Jogos / Treinos
              </h4>
              <Link
                to="/technical-committee/convocations/new"
                state={{
                  moduleId,
                  championshipId: id,
                  championshipName: formData.nome,
                  returnTab: "JOGOS",
                }}
                className="btn btn-sm btn-primary"
              >
                <KTSVG
                  path="/media/icons/duotune/arrows/arr075.svg"
                  className="svg-icon-2 me-1"
                />
                Adicionar Jogo / Treino
              </Link>
            </div>

            <div className="table-responsive w-100">
              <table className="table table-row-dashed align-middle gs-0 gy-4 w-100">
                <thead>
                  <tr className="fw-bold text-muted bg-light fs-7">
                    <th className="ps-4 rounded-start w-50px text-center"></th>
                    <th>Título / Evento</th>
                    <th>Adversário</th>
                    <th>Data/Hora</th>
                    <th>Local</th>
                    <th>Status</th>
                    <th className="text-end pe-4 rounded-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {jogos.length > 0 ? (
                    jogos.map((jogo) => (
                      <tr key={jogo._id_convocacao}>
                        <td className="ps-4 text-center">
                          <div className="symbol symbol-40px symbol-circle">
                            {jogo.adversario_logo ? (
                              <img
                                src={
                                  jogo.adversario_logo.startsWith("http")
                                    ? jogo.adversario_logo
                                    : `${S3_BASE_URL}/${jogo.adversario_logo}`
                                }
                                alt="Logo"
                                className="w-100 h-100 object-fit-cover"
                              />
                            ) : (
                              <span className="symbol-label bg-light-primary text-primary fw-bold fs-6">
                                {(jogo.adversario_nome || "A").charAt(0)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="fs-7">
                          <div className="d-flex flex-column">
                            <span className="text-dark fw-bold">
                              {jogo.titulo}
                            </span>
                            <span className="text-muted fs-8">
                              Convocação #{jogo._id_convocacao}
                            </span>
                          </div>
                        </td>
                        <td className="fs-7">{jogo.adversario_nome || "-"}</td>
                        <td className="fs-7">
                          {formatDate(jogo.data_apresentacao)}
                        </td>
                        <td className="fs-7">{jogo.local}</td>
                        <td className="fs-7">
                          <span
                            className={`badge badge-light-${
                              jogo.status === "aberta" ? "success" : "secondary"
                            }`}
                          >
                            {jogo.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="text-end p-0 m-0 pe-4 fs-7">
                          <Link
                            to={`/technical-committee/convocations/edit/${jogo._id_convocacao}`}
                            state={{
                              moduleId,
                              championshipId: id,
                              returnTab: "JOGOS",
                            }}
                            className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm"
                            title="Editar Jogo"
                          >
                            <KTSVG
                              path="/media/icons/duotune/art/art005.svg"
                              className="svg-icon-3"
                            />
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center p-5 text-muted">
                        Nenhum jogo cadastrado para este campeonato.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "CONVOCACAO" && (
          <div>
            <div className="alert alert-info w-100 text-center align-items-center p-5 mb-5">
              <div className="row">
                <div className="col-lg-3 text-end">
                  <KTSVG
                    path="/media/icons/duotune/general/gen048.svg"
                    className="svg-icon-2hx svg-icon-info me-4"
                  />
                </div>
                <div className="col-lg-9 text-start">
                  <h4 className="mb-1 text-info">
                    Gerenciar Convocações por Jogo
                  </h4>
                  <span>
                    Selecione a lista de atletas selecionados/convocados de
                    acordo com o evento desejado.
                  </span>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-row-dashed align-middle gs-0 gy-4">
                <thead>
                  <tr className="fw-bold text-muted bg-light fs-7">
                    <th className="ps-4 rounded-start">Jogo / Evento</th>
                    <th>Data</th>
                    <th>Local</th>
                    <th className="text-end pe-4 rounded-end">
                      Gerenciar Lista
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {jogos.length > 0 ? (
                    jogos.map((jogo) => (
                      <tr key={jogo._id_convocacao}>
                        <td className="ps-1 fs-7">
                          <span className="text-dark fw-bold">
                            {jogo.titulo}
                          </span>
                          {jogo.adversario_nome && (
                            <span className="text-muted ms-2">
                              vs {jogo.adversario_nome}
                            </span>
                          )}
                        </td>
                        <td className="fs-7">
                          {formatDate(jogo.data_apresentacao)}
                        </td>
                        <td className="fs-7">{jogo.local}</td>
                        <td className="text-end p-0 m-0 pe-4 fs-7">
                          <Link
                            to={`/technical-committee/convocations/${jogo._id_convocacao}/manage`}
                            state={{
                              moduleId,
                              championshipId: id,
                              returnTab: "CONVOCACAO",
                            }}
                            className="btn btn-sm btn-primary pe-1 me-0 ps-3 ms-0"
                            title="Lista de atletas seleciondados"
                          >
                            <KTSVG
                              path="/media/icons/duotune/communication/com014.svg"
                              className="svg-icon-2 me-2"
                            />
                          </Link>
                          &nbsp;
                          <button
                            type="button"
                            onClick={() =>
                              openConfirmConvocacao(jogo._id_convocacao)
                            }
                            className="btn btn-sm btn-success pe-1 me-0 ps-3 ms-0"
                            title="Convocar atletas selecionados"
                          >
                            <KTSVG
                              path="/media/icons/duotune/general/gen049.svg"
                              className="svg-icon-2 me-2"
                            />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center p-5 text-muted">
                        Nenhum evento disponível para convocação. Adicione jogos
                        ou treinos na aba anterior.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Modal
        show={showConfirmConvocacaoModal}
        onHide={() => setShowConfirmConvocacaoModal(false)}
        size="lg"
        centered
      >
        <Modal.Header className="pt-4 pb-2 mt-0 mb-0" closeButton>
          <Modal.Title className="fw-bold">Convocação de Atletas</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-gray-600 mt-0 mb-0">
            Abaixo estão os atletas selecionados para este jogo. Ao confirmar, o
            status será alterado para <strong>"Convocado"</strong>, e o atleta
            será notificado.
          </div>

          <div className="table-responsive" style={{ maxHeight: "400px" }}>
            <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3">
              <thead>
                <tr className="fw-bold text-muted fs-7 text-uppercase">
                  <th>Atleta</th>
                  <th style={{ width: "120px" }}>Posição</th>
                  <th style={{ width: "120px" }}>Status Atual</th>
                </tr>
              </thead>
              <tbody>
                {convocadosParaConfirmar.length > 0 ? (
                  convocadosParaConfirmar.map((atleta: any) => (
                    <tr key={atleta._id_convocado} className="p-0 m-0">
                      <td className="p-0 m-0">
                        <div className="d-flex align-items-center">
                          <div className="symbol symbol-35px me-3">
                            <span className="symbol-label bg-light-primary text-primary fw-bold">
                              {atleta.nome?.charAt(0)}
                            </span>
                          </div>
                          <span className="text-dark fw-bold fs-7">
                            {atleta.nome} {atleta.sobrenome}
                          </span>
                        </div>
                      </td>
                      <td className="fs-7 p-0 m-0">{atleta.posicao || "-"}</td>
                      <td className="p-0 m-0">
                        <span
                          className={`badge badge-light-${
                            atleta.status_presenca === "selecionado"
                              ? "warning"
                              : "primary"
                          } fs-8`}
                        >
                          {atleta.status_presenca}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-5">
                      Nenhum atleta selecionado para este jogo.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            className="btn-sm"
            onClick={() => setShowConfirmConvocacaoModal(false)}
            disabled={modalActionLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="success"
            className="btn-sm"
            onClick={processConvocacaoFinal}
            disabled={
              modalActionLoading || convocadosParaConfirmar.length === 0
            }
          >
            {modalActionLoading ? "Processando..." : "Convocar Selecionados"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
